import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, Heart, Award, Users, Globe2, ShieldCheck, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { PageHero, Reveal, SectionHeading, CountUp, Eyebrow } from "../components/common";
import { STATS, BRAND } from "../mock";

const VALUES = [
  { icon: Target, title: "Student-first", desc: "Every recommendation is built around your ambitions, budget and future — never a sales target." },
  { icon: ShieldCheck, title: "Integrity", desc: "Transparent costs, honest timelines and a shortlist that actually fits your profile." },
  { icon: Globe2, title: "Global reach", desc: "Foreign collaborations with institutions across Australia, UK, USA, Canada, Singapore, Malaysia and more." },
  { icon: Heart, title: "Care beyond boarding", desc: "Pre-departure briefings and on-ground support that don't stop when you fly." },
];

const DIFFERENTIATORS = [
  "A pioneer in overseas education since 1996",
  "One dedicated counsellor for your full journey",
  "A shortlist, not a catalogue — universities that fit",
  "Direct foreign collaborations, not just listings",
  "Study tours, internships and placements arranged",
  "A network of offices and agents across India",
];

const LEADERSHIP = [
  { name: "Mr. Ashish Pandit", role: "Chief Director & CEO", initials: "AP", bio: "A visionary leader with over two decades revolutionising overseas education. Under his direction, GDIH Elite has built key international university partnerships and launched pioneering initiatives — from the OSSD Canadian high-school pathway to skills and entrepreneurship programmes — empowering students across the subcontinent." },
  { name: "Ms. Sakina Bihir", role: "Chief Coordinator & Course Counsellor", initials: "SB", bio: "Guides students through course selection, applications and one-to-one counselling — ensuring every learner finds the right programme, university and country to match their ambitions and budget." },
];

export default function About() {
  return (
    <>
      <PageHero
        eyebrow="About Us"
        title="More than a consultancy — your career partners"
        subtitle={`${BRAND.name} is a full-service education group that has guided students to world-class universities and represented institutions worldwide for over 25 years. We build roadmaps, not brochures.`}
        image="https://images.unsplash.com/photo-1639765766830-d829d2fe4219"
      />

      <section className="section-pad bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <img src="https://images.unsplash.com/photo-1517486808906-6ca8b3f04846" alt="Students" className="rounded-2xl h-[420px] w-full object-cover shadow-lg" />
            <div className="absolute -bottom-6 -right-4 bg-[#c9a227] text-[#013d2b] rounded-2xl px-6 py-4 shadow-xl">
              <p className="font-display font-bold text-3xl"><CountUp end={500} suffix="+" /></p>
              <p className="text-sm font-semibold">Students placed</p>
            </div>
          </div>
          <div>
            <SectionHeading center={false} eyebrow="Our Story" title="Building global futures since 1996" />
            <p className="mt-5 text-slate-600 leading-relaxed">
              {BRAND.name} began with a simple belief: every student deserves access to world-class education, whatever their budget. Nearly three decades on, we've grown into a full-service education group — placing students in top universities, representing institutions abroad and setting up offshore campuses across the subcontinent.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              We represent renowned universities across Australia, New Zealand, UK, USA, Canada, Singapore, Malaysia, China and Indonesia, and combine human expertise with AI-powered program matching to give you a shortlist that genuinely fits — then walk with you through admissions, tests, visas and settling in abroad.
            </p>
            <div className="grid grid-cols-2 gap-4 mt-8">
              <div className="bg-[#f7faf8] rounded-xl p-5">
                <Target size={22} className="text-[#c9a227]" />
                <p className="font-semibold text-[#012a1e] mt-2">Our Mission</p>
                <p className="text-slate-500 text-sm mt-1">Make world-class education accessible, affordable and stress-free.</p>
              </div>
              <div className="bg-[#f7faf8] rounded-xl p-5">
                <Eye size={22} className="text-[#c9a227]" />
                <p className="font-semibold text-[#012a1e] mt-2">Our Vision</p>
                <p className="text-slate-500 text-sm mt-1">To be the most trusted global education partner in Asia.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#004030]">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.label} className="text-center">
              <p className="font-display font-bold text-4xl text-[#e0c05a]"><CountUp end={s.value} suffix={s.suffix} /></p>
              <p className="text-white font-semibold mt-1 text-sm">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow="Our Values" title="What we stand for" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 70}>
                <div className="bg-[#f7faf8] rounded-2xl p-7 h-full hover-lift border border-transparent hover:border-[#c9a227]/40">
                  <div className="w-14 h-14 rounded-xl bg-[#004030] grid place-items-center text-[#e0c05a]"><v.icon size={24} /></div>
                  <h3 className="font-display text-lg font-bold text-[#012a1e] mt-4">{v.title}</h3>
                  <p className="text-slate-500 text-sm mt-2 leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-white pt-0">
        <div className="max-w-5xl mx-auto px-6">
          <SectionHeading eyebrow="Leadership" title="The team behind GDIH Elite" />
          <div className="grid sm:grid-cols-2 gap-6 mt-14">
            {LEADERSHIP.map((p, i) => (
              <Reveal key={p.name} delay={i * 80}>
                <div className="h-full bg-[#f7faf8] rounded-2xl p-8 border border-slate-100" data-testid={`leader-${i}`}>
                  <div className="w-16 h-16 rounded-full bg-[#004030] grid place-items-center text-[#e0c05a] font-display font-bold text-xl">{p.initials}</div>
                  <h3 className="font-display text-xl font-bold text-[#012a1e] mt-5">{p.name}</h3>
                  <p className="text-[#c9a227] font-semibold text-sm mt-0.5">{p.role}</p>
                  <p className="text-slate-500 text-[15px] mt-3 leading-relaxed">{p.bio}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-[#f4f8f6]">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionHeading center={false} eyebrow="What Sets Us Apart" title="A partner, not just a portal" />
            <ul className="mt-8 space-y-4">
              {DIFFERENTIATORS.map((d) => (
                <li key={d} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle2 size={20} className="text-[#c9a227] shrink-0 mt-0.5" /> {d}
                </li>
              ))}
            </ul>
            <Link to="/contact"><Button className="mt-8 bg-[#004030] hover:bg-[#013d2b] text-white rounded-full h-12 px-7 font-semibold">Start your journey <ArrowRight size={17} className="ml-1" /></Button></Link>
          </div>
          <img src="https://images.unsplash.com/photo-1523240795612-9a054b0db644" alt="Counselling" className="rounded-2xl h-[420px] w-full object-cover shadow-lg" />
        </div>
      </section>
    </>
  );
}